import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import { colors } from '../../styles/colors';

const COOLDOWN_SEC = 60;

export default function EmailVerificationPage() {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const navigate = useNavigate();

  const firebaseUser = useAuthStore((s) => s.firebaseUser);
  const checkEmailVerified = useAuthStore((s) => s.checkEmailVerified);
  const resendVerificationEmail = useAuthStore((s) => s.resendVerificationEmail);
  const logout = useAuthStore((s) => s.logout);
  const clearPendingEmailVerification = useAuthStore((s) => s.clearPendingEmailVerification);

  const [checking, setChecking] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(COOLDOWN_SEC);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleCheck = useCallback(async () => {
    setChecking(true);
    setError('');
    try {
      const verified = await checkEmailVerified();
      if (verified) {
        navigate('/app', { replace: true });
      } else {
        setError(isKo ? '아직 인증이 완료되지 않았습니다.' : 'Email not verified yet.');
      }
    } catch {
      setError(isKo ? '확인 중 오류가 발생했습니다.' : 'Error checking verification status.');
    } finally {
      setChecking(false);
    }
  }, [checkEmailVerified, navigate, isKo]);

  const handleResend = async () => {
    setResending(true);
    setError('');
    setSuccess('');
    try {
      await resendVerificationEmail();
      setCooldown(COOLDOWN_SEC);
      setSuccess(isKo ? '인증 메일을 다시 발송했습니다.' : 'Verification email resent.');
    } catch {
      setError(isKo ? '메일 발송에 실패했습니다. 잠시 후 다시 시도해주세요.' : 'Failed to send email. Please try again later.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6"
      style={{ backgroundColor: colors.background }}>

      <div className="w-full max-w-[380px] rounded-xl p-6"
        style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>

        {/* Icon */}
        <div className="mb-4 text-center text-5xl">&#x2709;&#xFE0F;</div>

        {/* Title */}
        <h2 className="mb-2 text-center text-lg font-bold" style={{ color: colors.text }}>
          {isKo ? '이메일 인증' : 'Email Verification'}
        </h2>

        {/* Description */}
        <p className="mb-1 text-center text-sm leading-relaxed" style={{ color: colors.textSecondary }}>
          {isKo
            ? '인증 메일을 발송했습니다.'
            : 'A verification email has been sent.'}
        </p>
        <p className="mb-4 text-center text-xs" style={{ color: colors.textMuted }}>
          {firebaseUser?.email}
        </p>

        <p className="mb-5 text-center text-xs leading-relaxed" style={{ color: colors.textMuted }}>
          {isKo
            ? '이메일을 확인하고 인증 링크를 클릭한 후, 아래 "인증 완료" 버튼을 눌러주세요.'
            : 'Check your email and click the verification link, then press "Verify" below.'}
        </p>

        {/* Verify button */}
        <button onClick={handleCheck} disabled={checking}
          className="mb-3 w-full rounded-lg py-2.5 text-sm font-bold text-white disabled:opacity-50"
          style={{ backgroundColor: colors.primary }}>
          {checking
            ? (isKo ? '확인 중...' : 'Checking...')
            : (isKo ? '인증 완료' : 'I verified my email')}
        </button>

        {/* Resend button */}
        <button onClick={handleResend} disabled={resending || cooldown > 0}
          className="mb-3 w-full rounded-lg py-2.5 text-sm font-semibold disabled:opacity-40"
          style={{ backgroundColor: colors.card, color: colors.textSecondary, border: `1px solid ${colors.border}` }}>
          {cooldown > 0
            ? (isKo ? `재발송 (${cooldown}초)` : `Resend (${cooldown}s)`)
            : resending
              ? (isKo ? '발송 중...' : 'Sending...')
              : (isKo ? '인증 메일 재발송' : 'Resend verification email')}
        </button>

        {/* Status messages */}
        {error && <p className="mb-2 text-center text-xs" style={{ color: colors.error }}>{error}</p>}
        {success && <p className="mb-2 text-center text-xs" style={{ color: colors.success ?? colors.primary }}>{success}</p>}

        {/* Spam notice */}
        <p className="mb-4 text-center text-[10px] leading-relaxed" style={{ color: colors.textMuted }}>
          {isKo
            ? '* 메일이 보이지 않으면 스팸/정크 폴더를 확인해주세요.'
            : '* If you don\'t see the email, check your spam/junk folder.'}
        </p>

        {/* Logout / back */}
        <button onClick={async () => {
          clearPendingEmailVerification();
          await logout();
          navigate('/login', { replace: true });
        }}
          className="w-full text-center text-xs" style={{ color: colors.textMuted }}>
          {isKo ? '다른 계정으로 로그인' : 'Use a different account'}
        </button>
      </div>
    </div>
  );
}
